import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  catchError,
  EMPTY,
  expand,
  finalize,
  last,
  map,
  of,
  scan,
  Subject,
  switchMap,
  takeUntil
} from 'rxjs';
import { FileService } from 'src/app/services/api/file.service';
import { IncidentService } from 'src/app/services/api/incident.service';
import { DialogIncidentHistoryComponent } from '../dialog-incident-history/dialog-incident-history.component';
import { CsReviewTableRow } from '../incidents.types';

const REVISION_AC = 'REVISION_AC' as const;
@Component({
  selector: 'app-revision-ac',
  templateUrl: './revision-ac.component.html',
  styleUrls: ['./revision-ac.component.css']
})
export class RevisionAcComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  displayedColumns: string[] = [
    'incidentDate',
    'isApproved',
    'fileNumber',
    'nameClient',
    'fileProgrammer',
    'destination',
    'description',
    'incidentStatus',
    'details'
  ];
  csReviewDataSource = new MatTableDataSource<CsReviewTableRow>([]);

  readonly INCIDENT_STATUS = {
    PENDING: 'PENDING',
    RESOLVED: 'RESOLVED',
    APPROVED: 'APPROVED',
    NOT_APPROVED: 'NOT_APPROVED'
  } as const;

  readonly reviewTypes = ['Aprobado', 'Regularizar'];
  readonly destinationOptions = ['Lima', 'Cusco', 'Huaraz', 'Otros'];
  readonly statusOptions = [
    { value: this.INCIDENT_STATUS.PENDING, label: 'Creado' },
    { value: this.INCIDENT_STATUS.RESOLVED, label: 'Resuelto por Prog.' },
    { value: this.INCIDENT_STATUS.NOT_APPROVED, label: 'No Aprobado' },
    { value: this.INCIDENT_STATUS.APPROVED, label: 'Aprobado' }
  ];

  isLoading = true;
  loadingMessage = 'Cargando revisiones de Atención al Cliente...';
  hasError = false;
  showFilters = false;

  searchQuery = '';
  filterDate: Date | null = null;
  filterReviewType = '';
  filterDestination = '';
  filterStatus = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private readonly incidentService: IncidentService,
    private readonly fileService: FileService,
    private dialog: MatDialog
  ) {}

  openIncidentsHistorial(element: CsReviewTableRow) {
    this.dialog.open(DialogIncidentHistoryComponent, {
      width: '600px',
      data: {
        incidentType: REVISION_AC,
        historial: element.historial || []
      }
    });
  }

  ngOnInit(): void {
    this.setupFilterPredicate();
    this.getAllCsReviews();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupFilterPredicate() {
    this.csReviewDataSource.filterPredicate = (data: any, filter: string) => {
      const searchTerms = JSON.parse(filter);

      // 1. Text Search
      const textMatch =
        !searchTerms.query ||
        JSON.stringify(data)
          .toLowerCase()
          .includes(searchTerms.query.toLowerCase());

      // 2. Date Match
      let dateMatch = true;
      if (searchTerms.date) {
        const itemDate = new Date(data.incidentDate);
        const filterD = new Date(searchTerms.date);
        dateMatch = itemDate.toDateString() === filterD.toDateString();
      }

      // 3. Review Type Match (Mapped to description for revision-ac)
      let reviewTypeMatch = true;
      if (searchTerms.reviewType) {
        if (searchTerms.reviewType === 'Aprobado') {
          reviewTypeMatch =
            data.incidentStatus === this.INCIDENT_STATUS.APPROVED;
        }
        if (searchTerms.reviewType === 'Regularizar') {
          reviewTypeMatch =
            data.incidentStatus !== this.INCIDENT_STATUS.APPROVED;
        }
      }

      // 4. Destination Match
      let destMatch = true;
      if (searchTerms.destination) {
        if (searchTerms.destination === 'Otros') {
          destMatch = !this.destinationOptions
            .filter((destOption) => destOption !== 'Otros')
            .map((destOption) => destOption.toLowerCase())
            .includes(data.destination.toLowerCase());
        } else {
          destMatch =
            data.destination.trim().toLowerCase() ===
            searchTerms.destination.toLowerCase();
        }
      }

      // 5. Status Match
      let statusMatch = true;
      if (searchTerms.status) {
        statusMatch = data.incidentStatus === searchTerms.status;
      }

      return (
        textMatch && dateMatch && reviewTypeMatch && destMatch && statusMatch
      );
    };
  }

  applyFilters() {
    const filterObj = {
      query: this.searchQuery,
      date: this.filterDate,
      reviewType: this.filterReviewType,
      destination: this.filterDestination,
      status: this.filterStatus
    };
    this.csReviewDataSource.filter = JSON.stringify(filterObj);
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
    if (!this.showFilters) {
      this.filterDate = null;
      this.filterReviewType = '';
      this.filterDestination = '';
      this.filterStatus = '';
      this.applyFilters();
    }
  }

  refreshData() {
    this.getAllCsReviews();
  }

  private fetchAllCsReviews() {
    return this.incidentService
      .findAllCsReviews({
        startEvaluatedKeyId: undefined
      })
      .pipe(
        expand((response) => {
          const nextStartEvaluatedKeyId =
            response.findAllRevisionAC?.lastEvaluatedKeyId ?? undefined;

          if (!nextStartEvaluatedKeyId) {
            return EMPTY;
          }

          return this.incidentService.findAllCsReviews({
            startEvaluatedKeyId: nextStartEvaluatedKeyId
          });
        }),
        map((response) => response.findAllRevisionAC?.csReviews ?? []),
        scan((allCsReviews, pageCsReviews) => {
          const nextAllCsReviews = [...allCsReviews, ...pageCsReviews];
          this.loadingMessage = `Cargando revisiones de Atención al Cliente... ${nextAllCsReviews.length} registros cargados`;
          return nextAllCsReviews;
        }, [] as any[]),
        last()
      );
  }

  getAllCsReviews(): void {
    this.isLoading = true;
    this.hasError = false;
    this.csReviewDataSource.data = [];
    this.loadingMessage = 'Cargando revisiones de Atención al Cliente...';

    this.fetchAllCsReviews()
      .pipe(
        switchMap((allCsReviews) => {
          if (!allCsReviews.length) return of([]);

          const mappedCsReviews = allCsReviews.map((item: any) => {
            const adaptedIncident: CsReviewTableRow = {
              ...item
            };

            return adaptedIncident;
          });

          const allItineraryIds = Array.from(
            new Set(mappedCsReviews.map((review: any) => review.itineraryId))
          ) as string[];

          console.log({ mappedData: mappedCsReviews });
          if (allItineraryIds.length === 0) {
            return of(mappedCsReviews);
          }

          return this.getItinerariesByIds(allItineraryIds).pipe(
            map((itineraries: any[]) => {
              return mappedCsReviews.map((csReview: any) => {
                const matchedItinerary = itineraries.find(
                  (itinerary) => itinerary.id === csReview.itineraryId
                );
                return {
                  ...csReview,
                  fileProgrammer: matchedItinerary?.usuario || null,
                  fileNumber:
                    `${matchedItinerary?.fileNumber}${matchedItinerary?.fileExtend || ''}` ||
                    null,
                  nameClient: matchedItinerary?.nameClient || null
                };
              });
            })
          );
        }),
        catchError((err) => {
          console.error('Error fetching CsReviews:', err);
          this.hasError = true;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (mappedCsReviews) => {
          if (!this.hasError) {
            console.log({ mappedCsReviews });
            this.csReviewDataSource.data = mappedCsReviews;
            this.csReviewDataSource.paginator = this.paginator;
            this.csReviewDataSource.sort = this.sort;
            this.hasError = false;
            this.applyFilters();
          }
        }
      });
  }

  getItinerariesByIds(itineraryIds: string[]) {
    return this.fileService
      .getItineraryByIds({
        ids: itineraryIds,
        projectionExpression: 'id, fileNumber, fileExtend, nameClient, usuario'
      })
      .pipe(
        map((response: any) => {
          return response.getItineraryByIds || [];
        })
      );
  }
}
